import type { BombeoDetails, Muelle, ScaleEvent, SpecialEvent, SpecialEventType, StayOperation } from '../domain/scales'

/** A scale's key in the database: scale numbers repeat across years. */
export interface ScaleKey {
  year: number
  scaleId: string
}

/** A scale as stored: the domain scale plus its year and archive flag. */
/** Vessel details from SOSTRAT (V_TC_ATRACADA, V_TC_ATRAQUEOPERACIO); null until an import sets them. */
export interface VesselDetails {
  vessel: string | null
  imo: string | null
  /** Length overall, metres. */
  eslora: number | null
  /** Bow to centre manifold, metres. */
  bcm: number | null
  consignataria: string | null
  eta: string | null
  etd: string | null
  /** The ETA/ETD first imported: SOSTRAT later overwrites `eta`/`etd` with the actual times. */
  firstEta: string | null
  firstEtd: string | null
}

export interface ScaleRow extends ScaleKey, VesselDetails {
  isManual: boolean
  seenInSource: boolean
  /** Archived: hidden from the default list. */
  finished: boolean
}

/** What SOSTRAT reports about a berth stay (PLACE events); empty on other events. */
export interface StayDetails {
  /** Fondeo Previo: the vessel anchored before this stay. */
  fondeoPrevio: boolean | null
  /** The previous muelle, or the port of origin on a scale's first stay. */
  anterior: string | null
  /** Muelles alternativos autorizados (berth codes, possibly outside the muelle list). */
  muellesAlternativos: string[]
  /** SOSTRAT's own Terminal / Term. Add. columns (empty in the exports so far). */
  sostratTerminal: string | null
  sostratTermAdd: string | null
  /** Estat Atracada, e.g. "Finalizado"; null when SOSTRAT reports no operation. */
  sostratState: string | null
  /** The stay's cargo operations, in export order. */
  operations: StayOperation[]
}

/** The MARPOL annex a MARPOL event covers: I (oil) or V (garbage). */
export const MARPOL_ANNEXES = ['I', 'V'] as const
export type MarpolAnnex = (typeof MARPOL_ANNEXES)[number]

/** What EQ records on a MARPOL event. */
export interface MarpolDetails {
  /** Required on every MARPOL event saved; null on those recorded before it was. */
  marpolAnnex: MarpolAnnex | null
  /** HORA AVISO SERVICIO MARPOL: when the MARPOL service was requested. */
  marpolNoticeAt: string | null
}

export type EventDetails = StayDetails & BombeoDetails & MarpolDetails

export const NO_EVENT_DETAILS: EventDetails = {
  fondeoPrevio: null,
  anterior: null,
  muellesAlternativos: [],
  sostratTerminal: null,
  sostratTermAdd: null,
  sostratState: null,
  operations: [],
  connectionPoint: null,
  productClass: null,
  product: null,
  quantityT: null,
  operationKind: null,
  marpolAnnex: null,
  marpolNoticeAt: null,
}

/**
 * A stored event: the domain event (whose `scaleId` is the scale number) plus
 * the scale's year, the companies operating at a berth stay (`terminals`, from
 * SOSTRAT's Estibador: the field map's EMPRESA; empty for other events) and
 * the details above (each only on its event type).
 */
export type EventRow = ScaleEvent & { year: number; terminals: string[] } & EventDetails

export interface ScaleWithEventRows {
  scale: ScaleRow
  events: EventRow[]
}

/** A MARPOL/BOMBEO event as created or edited; the other type's fields are stored empty. */
export interface SpecialEventInput extends BombeoDetails, MarpolDetails {
  type: SpecialEventType
  startTime: string
  endTime: string | null
}

/** A connection point (arm or pipeline) of a muelle, and the terminal behind it. */
export interface ConnectionPoint {
  /** As labelled on the quay, e.g. '32C/TEP/T1'. */
  code: string
  muelle: Muelle
  terminal: string
  status: 'IN_SERVICE' | 'OUT_OF_SERVICE' | 'UNLABELLED'
  note: string | null
}

/** A read of SÒSTRAT (table `source_runs`): REQUESTED → RUNNING → SUCCEEDED or FAILED, or REQUESTED → FAILED. */
export const SOURCE_RUN_STATUSES = ['REQUESTED', 'RUNNING', 'SUCCEEDED', 'FAILED'] as const
export type SourceRunStatus = (typeof SOURCE_RUN_STATUSES)[number]
/** MANUAL: an admin asked for it (`sostrat-sync`); SCHEDULE: the hourly workflow. */
export type SourceRunTrigger = 'MANUAL' | 'SCHEDULE'

export interface SourceRun {
  id: string
  trigger: SourceRunTrigger
  status: SourceRunStatus
  requestedAt: string
  startedAt: string | null
  finishedAt: string | null
  /** What the run wrote, as its writer reports it (e.g. stays and operations), by name. */
  counts: Record<string, number>
  /** What stopped it. */
  problems: string[]
}

export const isRunInProgress = (run: Pick<SourceRun, 'status'>): boolean => run.status === 'REQUESTED' || run.status === 'RUNNING'

/**
 * What asking `sostrat-sync` for a read gives: the run it started (202), or the
 * one already in progress (409), which blocks a second read.
 */
export interface SyncStart {
  kind: 'started' | 'busy'
  run: Pick<SourceRun, 'id' | 'requestedAt'>
}

export type SourceErrorCode =
  | 'NOT_FOUND'
  | 'END_BEFORE_START'
  | 'NOT_SPECIAL'
  | 'SCALE_CLOSED'
  | 'NO_STAYS'
  | 'START_OUTSIDE_STAYS'
  /** `sostrat-sync` could not start the read (502: GitHub refused the dispatch); `message` says why. */
  | 'SYNC_REFUSED'

/** A refusal the UI can translate (`code`); `message` keeps the backend's detail. */
export class SourceError extends Error {
  constructor(
    readonly code: SourceErrorCode,
    message: string = code,
  ) {
    super(message)
    this.name = 'SourceError'
  }
}

/**
 * Where scales and their events live. The admin uses the Supabase source; tests
 * use the in-memory one. Rules that need the whole picture (current place,
 * summaries) are computed from these rows by `summarise`, with the shared domain
 * rules, so they stay identical to bombeo-app.
 */
export interface ScalesSource {
  /** Scales with all their events; `finished` filters by archive state when given. */
  listScales(options: { finished?: boolean }): Promise<ScaleWithEventRows[]>
  getScale(key: ScaleKey): Promise<ScaleWithEventRows | undefined>
  getEvent(id: string): Promise<EventRow | undefined>
  createSpecialEvent(key: ScaleKey, input: SpecialEventInput): Promise<EventRow & SpecialEvent>
  updateSpecialEvent(id: string, input: SpecialEventInput): Promise<EventRow & SpecialEvent>
  setFinished(key: ScaleKey, finished: boolean): Promise<ScaleRow>
  listConnectionPoints(): Promise<ConnectionPoint[]>
  /** Asks `sostrat-sync` to read SÒSTRAT now, with its default window, as the logged-in user. */
  startSostratSync(): Promise<SyncStart>
  /** The newest SÒSTRAT run, manual or scheduled; undefined before the first. */
  latestSostratRun(): Promise<SourceRun | undefined>
  getSourceRun(id: string): Promise<SourceRun | undefined>
}
